/**
 * M7 S40: clearing a derelict and delivering the restart kit at its repair port (plan S40; spec §16.3 steps 2–4,
 * economy plan M7-4 and M7-7).
 *
 * - **Hazards:** a reactor section's coolant anchor must be vented before the station can be restored
 *   (`ventCoolant`): from outside `salvage.json coolant.radius` it is cut safely; closer in, the ship takes M2's
 *   coolant damage (`coolant.damage`, 60) and the anchor is vented all the same.
 * - **Squatters:** a Wrecker squat is fought (the region's raider waves from the M3 reboot table, `squatWaves`; the
 *   caller marks it with `markSquatFought` once they are gone) or paid off with the fence fee (`payFence`, a wallet
 *   check first). A Holdfast family on a marked site is **never a fight**: hailing it (`hailFamily`) is a comms scene
 *   (a one-off hail line) that clears the way; the §16.6 choice comes at registration (S43).
 * - **The repair port** follows the M3 relink port rules (`relink/rules.ts portRefusal`: `portRangeM`,
 *   `portMaxSpeed`). It is the **new kit port**: it takes salvaged components and **any good** (`GoodId` units from
 *   the hold's `cargo.parts`, through `withGood`). The relay slots and the Reach save's relay `delivered` are not
 *   touched (M7-4).
 * - **Deliveries** (`deliverKit`): every kit-ready component in the hold whose type is still missing (Serviceable or
 *   better, consumed whole, never broken down: M7-7) and every missing good, as much as the hold carries. Progress
 *   persists in the Career record (`career.story.reactivations[].kit`, S15), so trips add up across saves. Cargo
 *   leaves the hold only for what the kit took (`deposited`). Rush-pattern parts go first and credit fuel cells
 *   (M8 MC-h4b, U14), so the goods pass then takes only the fuel cells still missing.
 *
 * Career in, Career out; pure. UI-rate events, never per frame.
 */
import { GOOD_NAMES, withGood, withWallet } from '../../career/ops'
import { removeComponent } from '../../career/cargo'
import { goodT, type GoodId } from '../../career/parts'
import type { Career } from '../../career/types'
import type { ComponentType, SalvageFileRaw } from '../../data/m2Schemas'
import salvageJson from '../../data/salvage.json'
import type { RegionId } from '../../galaxy/types'
import type { RelinkConfig } from '../../relink/config'
import { portRefusal, rebootSchedule, type ScheduledSpawn } from '../../relink/rules'
import type { Squatters } from '../../story/reactivation/eligibility'
import {
  acceptsComponent,
  deliverToKit,
  kitComplete,
  kitMissing,
  recordRequirement,
  replaceRecord,
  SALVAGE_BANDS,
  type KitDelivery,
  type SalvageBands,
} from '../../story/reactivation/kit'
import type { SurveyCard } from '../../story/reactivation/survey'
import type { ReactivationConfig } from '../../story/schemas'
import { storyOf } from '../../story/state'
import type { StoryEffect, StoryReactivation, SquatCleared } from '../../story/types'
import { fillTokens } from '../world'
import { reactivationText, type ReactivationText } from './survey'

/** The station facts clearing and deliveries read (from the survey card). */
export type SiteFacts = Pick<SurveyCard, 'poiId' | 'kind' | 'restorable' | 'coolantHazard' | 'squatters' | 'fenceFee' | 'markerFamily'>

export type RestoreRefusal =
  | 'noRecord'
  | 'closed'
  | 'harrow'
  | 'range'
  | 'speed'
  | 'hazard'
  | 'squatters'
  | 'family'
  | 'funds'
  | 'nothing'
  | 'noHazard'
  | 'noSquat'

/** The ship at the port: squared distance to the repair port (m²) and squared speed (m²/s²). */
export interface PortPosition {
  dist2: number
  speed2: number
}

/** The M2 coolant hazard (`salvage.json coolant`). */
export type CoolantConfig = Readonly<SalvageFileRaw['coolant']>
export const COOLANT: CoolantConfig = Object.freeze({ ...salvageJson.coolant })

const OPEN: ReadonlySet<StoryReactivation['state']> = new Set(['surveyed', 'kit', 'reverted'])

/** The Career's record for `poiId`, or undefined. */
export function recordOf(career: Career, poiId: string): StoryReactivation | undefined {
  return storyOf(career).reactivations.find((r) => r.poiId === poiId)
}

/** The record open for restoration work (surveyed, kit, reverted), or the refusal. */
function openRecord(career: Career, site: SiteFacts): StoryReactivation | RestoreRefusal {
  if (site.kind === 'harrow') return 'harrow'
  const r = recordOf(career, site.poiId)
  if (r === undefined || r.frClass === undefined) return 'noRecord'
  return OPEN.has(r.state) ? r : 'closed'
}

/** What still blocks work at the port (an unvented anchor, an uncleared squat), or null. */
export function clearRefusal(site: SiteFacts, record: StoryReactivation): RestoreRefusal | null {
  if (site.coolantHazard && record.vented !== true) return 'hazard'
  if (record.cleared === undefined) {
    if (site.squatters === 'wreckers') return 'squatters'
    if (site.squatters === 'holdfastFamily') return 'family'
  }
  return null
}

export type ClearResult =
  | { ok: true; career: Career; effects: StoryEffect[]; message: string; hullDamage: number }
  | { ok: false; reason: RestoreRefusal }

function ok(career: Career, message: string, hullDamage = 0, effects: StoryEffect[] = []): ClearResult {
  return { ok: true, career, effects, message, hullDamage }
}

/**
 * Vents the coolant anchor. `dist2ToAnchor` is the ship's squared distance to the reactor section: outside
 * `coolant.radius` the cut is safe, inside it the ship takes `coolant.damage` (`hullDamage`, the caller applies it).
 * Venting twice changes nothing.
 */
export function ventCoolant(
  career: Career,
  site: SiteFacts,
  dist2ToAnchor: number,
  coolant: CoolantConfig = COOLANT,
  text: ReactivationText = reactivationText(),
): ClearResult {
  const r = openRecord(career, site)
  if (typeof r === 'string') return { ok: false, reason: r }
  if (!site.coolantHazard) return { ok: false, reason: 'noHazard' }
  if (r.vented === true) return ok(career, text.messages.vented)
  const next = replaceRecord(career, { ...r, vented: true })
  const hot = dist2ToAnchor < coolant.radius * coolant.radius
  return hot ? ok(next, fillTokens(text.messages.ventedHot, { damage: String(coolant.damage) }), coolant.damage) : ok(next, text.messages.vented)
}

function markCleared(career: Career, r: StoryReactivation, how: SquatCleared): Career {
  return replaceRecord(career, { ...r, cleared: how })
}

/** Pays a Wrecker squat off with the fence fee (refused short of funds; the wallet is checked first). */
export function payFence(career: Career, site: SiteFacts, text: ReactivationText = reactivationText()): ClearResult {
  const r = openRecord(career, site)
  if (typeof r === 'string') return { ok: false, reason: r }
  if (site.squatters !== 'wreckers' || r.cleared !== undefined) return { ok: false, reason: 'noSquat' }
  if (career.wallet < site.fenceFee) return { ok: false, reason: 'funds' }
  return ok(markCleared(withWallet(career, -site.fenceFee), r, 'paid'), text.messages.paid)
}

/** The Wrecker squat's waves: the region's raider waves from the M3 reboot table (no guardians). */
export function squatWaves(region: RegionId, relink: RelinkConfig): ScheduledSpawn[] {
  return rebootSchedule(region, relink.reboot.seconds[region], false, relink)
}

/** The squat was fought off (the caller saw its waves gone). */
export function markSquatFought(career: Career, site: SiteFacts, text: ReactivationText = reactivationText()): ClearResult {
  const r = openRecord(career, site)
  if (typeof r === 'string') return { ok: false, reason: r }
  if (site.squatters !== 'wreckers' || r.cleared !== undefined) return { ok: false, reason: 'noSquat' }
  return ok(markCleared(career, r, 'fought'), text.messages.fought)
}

/**
 * Hails the Holdfast family on a marked site: a comms scene (a one-off hail line), never a fight. It clears the way;
 * the family's choice is made at registration (spec §16.6, S43).
 */
export function hailFamily(career: Career, site: SiteFacts, text: ReactivationText = reactivationText()): ClearResult {
  const r = openRecord(career, site)
  if (typeof r === 'string') return { ok: false, reason: r }
  if (site.squatters !== 'holdfastFamily' || r.cleared !== undefined) return { ok: false, reason: 'noSquat' }
  const line = fillTokens(text.messages.familyHail, { family: site.markerFamily ?? '' })
  const effects: StoryEffect[] = [{ type: 'callback', callbackId: `reactivation.family:${site.poiId}`, channel: 'comms', text: line }]
  return ok(markCleared(career, r, 'family'), text.messages.family, 0, effects)
}

// ---------------------------------------------------------------------------------------------
// The kit repair port
// ---------------------------------------------------------------------------------------------

/** What a delivery took out of the hold. */
export interface Deposited {
  /** Hold component ids consumed whole, by type. */
  components: { id: string; type: ComponentType }[]
  goods: Partial<Record<GoodId, number>>
}

export type DeliverResult =
  | { ok: true; career: Career; deposited: Deposited; complete: boolean; message: string }
  | { ok: false; reason: RestoreRefusal }

/** The port rule for a delivery or the restart's start (range and the speed limit). */
export function portCheck(at: PortPosition, relink: Pick<RelinkConfig, 'portRangeM' | 'portMaxSpeed'>): RestoreRefusal | null {
  return portRefusal(at.dist2, at.speed2, true, relink)
}

function depositedText(d: Deposited, text: ReactivationText): string {
  const counts = new Map<ComponentType, number>()
  for (const c of d.components) counts.set(c.type, (counts.get(c.type) ?? 0) + 1)
  const bits: string[] = []
  for (const [t, n] of counts) bits.push(`${n} ${text.components[t]}`)
  for (const [g, n] of Object.entries(d.goods) as [GoodId, number][]) bits.push(`${n} t ${GOOD_NAMES[g]}`)
  return bits.join(' + ')
}

/**
 * Delivers what the kit still needs from the hold at the derelict's repair port: refused away from the port, too
 * fast, before the hazards and squatters are cleared, or with nothing the kit needs aboard. Components below
 * Serviceable are never taken.
 */
export function deliverKit(
  career: Career,
  site: SiteFacts,
  at: PortPosition,
  cfg: ReactivationConfig,
  relink: Pick<RelinkConfig, 'portRangeM' | 'portMaxSpeed'>,
  bands: SalvageBands = SALVAGE_BANDS,
  text: ReactivationText = reactivationText(),
): DeliverResult {
  const record = openRecord(career, site)
  if (typeof record === 'string') return { ok: false, reason: record }
  const port = portCheck(at, relink)
  if (port !== null) return { ok: false, reason: port }
  const blocked = clearRefusal(site, record)
  if (blocked !== null) return { ok: false, reason: blocked }
  const req = recordRequirement(record, cfg)
  if (req === null) return { ok: false, reason: 'noRecord' }
  const deposited: Deposited = { components: [], goods: {} }
  let next = career
  let credited = 0
  // Components: whole, kit-ready, only while that type is still missing (deliverToKit refuses the rest). Rush-pattern
  // parts go first so their fuel-cell bonus lands (MC-h4b); the rest keep hold order.
  const rushFirst = [...career.cargo.components].sort((a, b) => Number(b.provenance === 'rush') - Number(a.provenance === 'rush'))
  for (const c of rushFirst) {
    if (!acceptsComponent(c.condition, bands)) continue
    const missing = kitMissing(req, recordOf(next, site.poiId)?.kit).components[c.type] ?? 0
    if (missing <= 0) continue
    const delivery: KitDelivery = { kind: 'component', type: c.type, condition: c.condition }
    if (c.provenance !== undefined) delivery.provenance = c.provenance
    const r = deliverToKit(next, site.poiId, delivery, cfg, bands)
    if (!r.ok) continue
    const removed = removeComponent(r.career, c.id)
    if (!removed.ok) continue
    next = removed.value
    credited += r.credited
    deposited.components.push({ id: c.id, type: c.type })
  }
  // Goods: whole units, as much as the hold carries, never more than is missing.
  for (const [g, need] of Object.entries(kitMissing(req, recordOf(next, site.poiId)?.kit).goods) as [GoodId, number][]) {
    const have = Math.floor(goodT(next.cargo.parts, g) + 1e-9)
    if (have <= 0 || need <= 0) continue
    const r = deliverToKit(next, site.poiId, { kind: 'good', good: g, qty: have }, cfg, bands)
    if (!r.ok) continue
    next = withGood(r.career, g, -r.taken)
    deposited.goods[g] = r.taken
  }
  if (deposited.components.length === 0 && Object.keys(deposited.goods).length === 0) return { ok: false, reason: 'nothing' }
  const complete = kitComplete(req, recordOf(next, site.poiId)?.kit)
  const rush = credited > 0 ? ` ${fillTokens(text.messages.rushCredit, { cells: String(credited) })}` : ''
  const message = fillTokens(text.messages.delivered, { what: depositedText(deposited, text) }) + rush + (complete ? ` ${text.messages.kitComplete}` : '')
  return { ok: true, career: next, deposited, complete, message }
}

/** The refusal's player-facing line. */
export function refusalText(reason: RestoreRefusal, relink: Pick<RelinkConfig, 'portRangeM'>, text: ReactivationText = reactivationText()): string {
  switch (reason) {
    case 'range':
      return fillTokens(text.refusals.range, { range: String(Math.round(relink.portRangeM)) })
    case 'noHazard':
    case 'noSquat':
      return text.refusals.closed
    default:
      return text.refusals[reason]
  }
}

/** The squatters a site still has (none once cleared). */
export function squattersLeft(site: SiteFacts, record: StoryReactivation | undefined): Squatters {
  return record?.cleared !== undefined ? 'none' : site.squatters
}
